import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Api } from "../lib/api.js";
import { setBearer } from "../lib/auth.js";

/**
 * First-run pairing page (displayed on the TV / unauthenticated device).
 *
 * On mount we ask the server for a short-lived pairing code, render a QR
 * pointing at the claim URL, and poll for the result. The user scans the QR
 * with their phone, signs in with Google on the claim page, and the TV's
 * next poll picks up the issued session token.
 *
 * If the user can't or won't use Google sign-in, they can hit the small
 * "Use a bearer token instead" link at the bottom to fall back to the manual
 * paste flow.
 */
export function Pair(): JSX.Element {
  const nav = useNavigate();
  const [state, setState] = useState<
    | { kind: "starting" }
    | { kind: "ready"; code: string; claimUrl: string }
    | { kind: "expired" }
    | { kind: "error"; message: string }
  >({ kind: "starting" });
  const [showManual, setShowManual] = useState(false);
  const [token, setToken] = useState("");
  const pollRef = useRef<number | null>(null);

  async function start(): Promise<void> {
    setState({ kind: "starting" });
    try {
      const r = await Api.pairing.start();
      const origin = window.location.origin;
      const claimUrl = `${origin}/claim?code=${encodeURIComponent(r.code)}`;
      setState({ kind: "ready", code: r.code, claimUrl });
    } catch (err) {
      setState({ kind: "error", message: (err as Error).message });
    }
  }

  useEffect(() => {
    void start();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.kind !== "ready") return;
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      try {
        const s = await Api.pairing.status(state.code);
        if (s.status === "approved" && s.token) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          setBearer(s.token);
          // Pull a fresh page so all useQuery caches refetch with the new
          // bearer attached, and the Layout / Home re-evaluate auth.
          window.location.replace("/");
        } else if (s.status === "expired") {
          // Auto-refresh — don't require the user to click a button.
          // This also recovers from server restarts (which clear in-memory codes).
          if (pollRef.current) window.clearInterval(pollRef.current);
          void start();
        }
      } catch {
        // network blip — keep polling
      }
    }, 2500);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [state.kind, state.kind === "ready" ? state.code : ""]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-lacquer-deep">
      <div className="card max-w-2xl w-full p-10 text-center relative overflow-hidden">
        <div className="section-label text-gold">— PAIR YOUR DEVICE —</div>
        <h1 className="hero-title mt-3">Scan to sign in</h1>
        <div className="hero-underline mt-4 mx-auto w-72" />
        <p className="text-parchment mt-6 max-w-[44ch] mx-auto">
          Point your phone's camera at the code below. You'll be asked to sign in
          with Google once — after that, this device is paired.
        </p>

        <div className="mt-8 flex flex-col items-center gap-4">
          {state.kind === "starting" && (
            <div className="skeleton w-[240px] h-[240px] rounded-lg" />
          )}
          {state.kind === "ready" && (
            <>
              <div className="bg-ivory p-4 rounded-lg" aria-label="Pairing QR code">
                <QRCodeSVG value={state.claimUrl} size={240} level="M" />
              </div>
              <div className="font-display text-goldHi text-3xl tracking-[0.4em] tabular-nums">
                {state.code}
              </div>
              <div className="text-parchment/70 text-xs">
                If you can't scan, open{" "}
                <span className="text-gold break-all">{state.claimUrl}</span>{" "}
                on your phone.
              </div>
            </>
          )}
          {state.kind === "expired" && (
            <div className="card-elev p-4 rounded-md">
              <div className="text-parchment">That code expired.</div>
              <button className="pill mt-3" onClick={() => void start()}>Show a new code</button>
            </div>
          )}
          {state.kind === "error" && (
            <div className="text-vermilionHi">{state.message}</div>
          )}
        </div>

        <div className="mt-10 border-t border-hairline pt-4">
          {!showManual ? (
            <button
              className="text-parchment/60 text-xs hover:text-gold underline"
              onClick={() => setShowManual(true)}
            >
              Or paste a bearer token instead
            </button>
          ) : (
            <form
              className="flex flex-col items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (token.trim()) {
                  setBearer(token.trim());
                  nav("/", { replace: true });
                }
              }}
            >
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="bearer token from /etc/wallace-tv.env"
                className="bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none font-mono text-sm w-full max-w-md"
              />
              <button type="submit" className="pill" disabled={token.trim() === ""}>
                Enter the theatre
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
